import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken, geocodePlace, reverseGeocode } from '../../services/mapService';
import './MapSelector.css';

/**
 * MapSelector Component
 * 
 * Interactive map for selecting locations using Mapbox
 * 
 * @param {Object} props
 * @param {Function} props.onSelectLocation - Callback when location is selected
 * @param {Function} props.onClose - Callback when map is closed
 * @param {Object} props.initialLocation - Optional initial location to display
 */
const MapSelector = ({ onSelectLocation, onClose, initialLocation = null }) => {
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  
  // Load Mapbox map when component mounts
  useEffect(() => {
    const token = getMapboxToken();
    if (!token) {
      console.error('Mapbox token not found');
      return;
    }
    
    // Set the access token
    mapboxgl.accessToken = token;
    
    // Set initial center point (default to New York if no initial location)
    const center = initialLocation 
      ? [initialLocation.lng, initialLocation.lat]
      : [-74.0060, 40.7128]; // New York
    
    // Create the map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: center,
      zoom: initialLocation ? 12 : 2 // Zoom in if we have a location, otherwise show world
    });
    
    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Create marker if we have an initial location
    if (initialLocation) {
      marker.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([initialLocation.lng, initialLocation.lat])
        .addTo(map.current);
        
      // Set up drag end event
      marker.current.on('dragend', handleMarkerDragEnd);
    }
    
    // Wait for map to load before adding click handler
    map.current.on('load', () => {
      map.current.on('click', handleMapClick);
      setMapInitialized(true);
    });
    
    // Cleanup function
    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // Empty dependency array means this runs once on mount
  
  // Handle map clicks
  const handleMapClick = async (e) => {
    const { lng, lat } = e.lngLat;
    
    try {
      // Reverse geocode to get place name
      const placeData = await reverseGeocode(lat, lng);
      
      // Update marker position
      if (marker.current) {
        marker.current.setLngLat([lng, lat]);
      } else {
        marker.current = new mapboxgl.Marker({ draggable: true })
          .setLngLat([lng, lat])
          .addTo(map.current);
          
        // Set up drag end event
        marker.current.on('dragend', handleMarkerDragEnd);
      }
      
      // Update selected location
      setSelectedLocation({
        name: placeData.name,
        lat: lat,
        lng: lng
      });
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      
      // Even if geocoding fails, still set the location with coordinates
      setSelectedLocation({
        name: `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        lat: lat,
        lng: lng
      });
    }
  };
  
  // Handle marker drag end
  const handleMarkerDragEnd = async () => {
    if (!marker.current) return;
    
    const { lng, lat } = marker.current.getLngLat();
    
    try {
      // Reverse geocode to get place name
      const placeData = await reverseGeocode(lat, lng);
      
      // Update selected location
      setSelectedLocation({
        name: placeData.name,
        lat: lat,
        lng: lng
      });
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      
      // Even if geocoding fails, still set the location with coordinates
      setSelectedLocation({
        name: `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        lat: lat,
        lng: lng
      });
    }
  };
  
  // Handle search
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    try {
      setIsSearching(true);
      const results = await geocodePlace(searchQuery);
      setSearchResults(results);
      setIsSearching(false);
    } catch (error) {
      console.error('Error searching for places:', error);
      setIsSearching(false);
      setSearchResults([]);
    }
  };
  
  // Handle search result selection
  const handleSelectSearchResult = (result) => {
    // Center map on the selected result
    map.current.flyTo({
      center: [result.lng, result.lat],
      zoom: 12,
      essential: true
    });
    
    // Update marker position
    if (marker.current) {
      marker.current.setLngLat([result.lng, result.lat]);
    } else {
      marker.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([result.lng, result.lat])
        .addTo(map.current);
        
      // Set up drag end event
      marker.current.on('dragend', handleMarkerDragEnd);
    }
    
    // Update selected location
    setSelectedLocation({
      name: result.name,
      lat: result.lat,
      lng: result.lng
    });
    
    // Clear search results
    setSearchResults([]);
    setSearchQuery('');
  };
  
  const handleConfirm = () => {
    if (selectedLocation) {
      onSelectLocation(selectedLocation);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <div className="map-selector-overlay">
      <div className="map-selector-container">
        <div className="map-selector-header">
          <h3>Select Location</h3>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>
        
        <div className="map-search-container">
          <form onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="map-search-input"
            />
            <button 
              type="submit" 
              className="map-search-button"
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </form>
          
          {searchResults.length > 0 && (
            <div className="search-results">
              <ul>
                {searchResults.map((result, index) => (
                  <li 
                    key={index}
                    onClick={() => handleSelectSearchResult(result)}
                  >
                    {result.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <div 
          className="map-container" 
          data-testid="map-container"
          ref={mapContainer}
        />
        
        {selectedLocation && (
          <div className="selected-location">
            <strong>Selected:</strong> {selectedLocation.name}
          </div>
        )}
        
        <div className="map-selector-footer">
          <button 
            className="cancel-button" 
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button 
            className="confirm-button" 
            onClick={handleConfirm}
            disabled={!selectedLocation}
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapSelector; 